import React, { useState } from "react"
import { Navigation, useNavigationContext } from "@kit/design/navigation"
import { TrialIndicator } from "@kit/design/trial-indicator"
import { ProgressCircle } from "@kit/design/progress-circle"
import { CountBadge } from "@kit/design/count-badge"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"
import mount from "core/mount"
import { Link } from "core/link"
import { useCurrentUser } from "hooks/use-current-user"
import { StatusBanner } from "./utils/status-banner"
import { UserAccountMenu } from "./user-account-menu"
import { useBulkNotifications } from "./utils/use-bulk-notifications"
import { DiscoveryHub } from "app/discovery-hub"
import KitLogo from "../../../assets/images/kit-logo-black.svg"

function NavigationHeader() {
  const { collapsed } = useNavigationContext()
  return (
    <Navigation.Header>
      <div className="relative flex items-center gap-1 h-6">
        {!collapsed && (
          <>
            <KitLogo style={{ height: 24, margin: 8 }} aria-hidden />
            <StatusBanner />
          </>
        )}
      </div>

      <Navigation.Trigger />
    </Navigation.Header>
  )
}

function NavigationFooter({ user, onUpgrade, renderLink, onWhatsNewClick, whatsNewCount, notificationsLeading, completedCount }) {
  return (
    <Navigation.Footer>
      {user.trialing && (
        <Navigation.FooterCallout>
          <TrialIndicator
            daysRemaining={user.trial_days_remaining ?? 0}
            daysTotal={user.trial_days_total ?? 0}
            onUpgrade={onUpgrade}
          />
        </Navigation.FooterCallout>
      )}
      <Navigation.Menu>
        {notificationsLeading && (
          <Navigation.Item
            leading={notificationsLeading}
            label="Notifications"
            trailing={completedCount > 0 ? <CountBadge count={completedCount} /> : null}
          />
        )}
        <Navigation.Item
          leading="settings-cog"
          label="Settings"
          pathPrefix="/account_settings"
          render={renderLink("/account_settings")}
        />
      </Navigation.Menu>
      <UserAccountMenu
        user={user}
        onWhatsNewClick={onWhatsNewClick}
        whatsNewCount={whatsNewCount}
      />
    </Navigation.Footer>
  )
}

export function AppNavigation({ currentPath: currentPathProp, renderLink = href => <Link href={href} />, onCollapsedChange } = {}) {
  const user = useCurrentUser()
  const currentPath = currentPathProp ?? window.location.pathname
  const nav = path => ({ pathPrefix: path, render: renderLink(path) })
  const [discoveryHubOpen, setDiscoveryHubOpen] = useState(false)
  const [whatsNewCount, setWhatsNewCount] = useState(0)
  const { activeTopic, progress, completedCount, hasTopics } = useBulkNotifications()
  const notificationsLeading = hasTopics
    ? activeTopic ? <ProgressCircle progress={progress} /> : "alarm"
    : null
  const isMobile = !useMediaBreakpoint("md")

  return (
    <Navigation currentPath={currentPath} mobile={isMobile} onCollapsedChange={onCollapsedChange}>
      <Navigation.Sidebar>
        <NavigationHeader />

        <Navigation.PrimaryAction icon="pencil-write" label="Start writing" render={<Link href="/campaigns/new/steps" method="post" />} />

        <Navigation.Menu>
          <Navigation.Item leading="dashboard" label="Dashboard" {...nav("/dashboard")} />
          <Navigation.Item leading="three-people" label="Subscribers" {...nav("/subscribers")} />
          <Navigation.Item leading="megaphone" label="Broadcasts" {...nav("/campaigns")} />

          <Navigation.Group icon="trending-up" label="Audience growth">
            <Navigation.GroupItem label="Landing Pages & Forms" {...nav("/forms")} />
            <Navigation.GroupItem label="Recommendations" {...nav("/creator-network")} />
            <Navigation.GroupItem label="Newsletter site" {...nav("/creator_profile")} />
          </Navigation.Group>

          <Navigation.Group icon="money" label="Monetization">
            <Navigation.GroupItem label="Products" {...nav("/products")} />
            {user.canViewAds && (
              <Navigation.GroupItem label="Newsletter Sponsorships" {...nav("/ads")} />
            )}
            {user.canUsePaidRecommendations && (
              <Navigation.GroupItem label="Paid Recommendations" {...nav("/paid_recommendations")} />
            )}
            <Navigation.GroupItem label="Tips" {...nav("/tips")} />
            <Navigation.GroupItem label="Payouts" {...nav("/account_settings/commerce")} />
          </Navigation.Group>

          <Navigation.Group icon="bolt" label="Automation">
            <Navigation.GroupItem label="Visual Automations" {...nav("/automations")} />
            <Navigation.GroupItem label="Rules" {...nav("/rules")} />
            <Navigation.GroupItem label="Email Sequences" {...nav("/sequences")} />
            <Navigation.GroupItem label="Kit MCP" {...nav("/kit-mcp")} />
            <Navigation.GroupItem label="RSS" {...nav("/feeds")} />
          </Navigation.Group>

          {user.accountCanAccessApps && (
            <Navigation.Item leading="app-store" label="Apps" {...nav("/apps")} />
          )}
        </Navigation.Menu>

        <NavigationFooter
          user={user}
          onUpgrade={() => {
            window.location.href = "/account/upgrade"
          }}
          renderLink={renderLink}
          onWhatsNewClick={() => setDiscoveryHubOpen(true)}
          whatsNewCount={whatsNewCount}
          notificationsLeading={notificationsLeading}
          completedCount={completedCount}
        />
      </Navigation.Sidebar>

      <Navigation.Dock>
        <Navigation.Dock.Trigger />
        <Navigation.Dock.Actions>
          <Navigation.Dock.Action label="Dashboard" icon="dashboard" {...nav("/dashboard")} />
          <Navigation.Dock.Action label="Subscribers" icon="three-people" {...nav("/subscribers")} />
          <Navigation.Dock.Action label="Broadcasts" icon="megaphone" {...nav("/campaigns")} />
        </Navigation.Dock.Actions>
        <Navigation.Dock.PrimaryAction label="Start writing" icon="pencil-write" render={<Link href="/campaigns/new/steps" />} />
      </Navigation.Dock>

      <DiscoveryHub
        open={discoveryHubOpen}
        onClose={() => setDiscoveryHubOpen(false)}
        onNotificationCount={setWhatsNewCount}
      />
    </Navigation>
  )
}

mount({ AppNavigation })
